import type {FileDiffReference} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {attr, controller, target} from '@github/catalyst'

/** Tracks all active `CopilotDiffEntryElement` instances. */
class CopilotDiffEntryStore extends EventTarget {
  #entries: readonly CopilotDiffEntryElement[] = []

  get entries() {
    return this.#entries
  }

  add(entry: CopilotDiffEntryElement) {
    this.#entries = this.#entries.concat(entry)
    this.dispatchEvent(new Event('update'))
  }

  remove(entry: CopilotDiffEntryElement) {
    this.#entries = this.#entries.filter(e => e !== entry)
    this.dispatchEvent(new Event('update'))
  }
}

@controller
export class CopilotDiffEntryElement extends HTMLElement {
  static readonly store = new CopilotDiffEntryStore()

  @attr fileDiffReferenceJson: string

  @target menuItemsSlot: HTMLElement | undefined
  @target floatingMenuSlot: HTMLElement | undefined

  connectedCallback() {
    // On page navigation the React app doesn't always clean up properly, and if we then navigate back with back/forward,
    // the HTML gets restored and these can load with children inside them. Then the portal appends to the container, causing
    // items to appear more than once.
    this.menuItemsSlot?.replaceChildren()
    this.floatingMenuSlot?.replaceChildren()
    CopilotDiffEntryElement.store.add(this)
  }

  disconnectedCallback() {
    CopilotDiffEntryElement.store.remove(this)
  }

  // cache so we can return same object reference if unchanged
  #diffReferenceCache?: {raw: string; parsed: FileDiffReference}

  get fileDiffReference() {
    const raw = this.fileDiffReferenceJson
    if (!raw) return null

    if (this.#diffReferenceCache?.raw === raw) return this.#diffReferenceCache.parsed

    const parsed = JSON.parse(raw) as FileDiffReference
    this.#diffReferenceCache = {raw, parsed}
    return parsed
  }
}
